import {
  createContext, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore,
  type ReactNode, type Ref, type RefObject, type ComponentPropsWithRef, type CSSProperties,
} from 'react';
import { LazyMotion, MotionConfig, domMax, m, usePresence } from 'motion/react';
import { animate } from 'motion/mini';

export const motionEase = [0.22, 1, 0.36, 1] as const;
const KeyboardContext = createContext(false);
const reducedQuery = '(prefers-reduced-motion: reduce)';

function subscribeReduced(callback: () => void) {
  const query = window.matchMedia?.(reducedQuery);
  query?.addEventListener('change', callback);
  return () => query?.removeEventListener('change', callback);
}

function reducedSnapshot() {
  return typeof window !== 'undefined' && (window.matchMedia?.(reducedQuery).matches ?? false);
}

export function useMotionPolicy() {
  const keyboard = useContext(KeyboardContext);
  const reduced = useSyncExternalStore(subscribeReduced, reducedSnapshot, () => true);
  const supported = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  return { reduced, instant: reduced || keyboard || !supported };
}

export function MotionProvider({ children }: { children: ReactNode }) {
  const [keyboard, setKeyboard] = useState(false);
  const { reduced } = useMotionPolicy();
  useEffect(() => {
    const setInput = (value: boolean) => {
      document.documentElement.dataset.inputMethod = value ? 'keyboard' : 'pointer';
      setKeyboard(value);
    };
    const onKey = () => setInput(true);
    const onPointer = () => setInput(false);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
      delete document.documentElement.dataset.inputMethod;
    };
  }, []);
  return <KeyboardContext.Provider value={keyboard}>
    <LazyMotion features={domMax} strict>
      <MotionConfig reducedMotion={reduced ? 'always' : 'never'} transition={{ duration: keyboard || reduced ? 0 : 0.2, ease: motionEase }}>
        {children}
      </MotionConfig>
    </LazyMotion>
  </KeyboardContext.Provider>;
}

export function AnimatedSurface({ children, origin = 'top right', variant = 'popover', style, role, ref, ...props }:
  ComponentPropsWithRef<'div'> & { origin?: string; variant?: 'popover' | 'toast'; ref?: Ref<HTMLDivElement> }) {
  const { instant } = useMotionPolicy();
  const [present, safeToRemove] = usePresence();
  useEffect(() => {
    if (present) return;
    if (instant) { safeToRemove?.(); return; }
    const timer = window.setTimeout(() => safeToRemove?.(), 120);
    return () => window.clearTimeout(timer);
  }, [present, instant, safeToRemove]);
  return <div {...props} ref={ref} role={present ? role : undefined} aria-hidden={present ? props['aria-hidden'] : true}
    inert={!present || props.inert} data-motion-surface="" data-present={present}
    style={{ ...style, opacity: 1, transformOrigin: origin,
      '--surface-offset': variant === 'toast' ? '12px' : '-5px',
      animation: instant ? 'none' : `${present ? 'bckt-surface-enter' : 'bckt-surface-exit'} ${present ? 200 : 120}ms cubic-bezier(0.22, 1, 0.36, 1) both`,
      pointerEvents: present ? style?.pointerEvents : 'none' } as CSSProperties}>
    {children}
  </div>;
}

export function ActiveNavigation({ active, id }: { active: boolean; id: string }) {
  const { instant } = useMotionPolicy();
  return active ? <m.span className="motion-nav-indicator" aria-hidden="true" layoutId={id}
    transition={instant ? { duration: 0 } : { type: 'spring', duration: 0.26, bounce: 0 }} /> : null;
}

export function useContentEntrance(ref: RefObject<HTMLElement | null>, identity: string) {
  const { instant } = useMotionPolicy();
  const previous = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (previous.current === identity) return;
    previous.current = identity;
    if (instant || !ref.current) return;
    const animation = animate(ref.current, { opacity: [0.65, 1] }, { duration: 0.16, ease: motionEase });
    return () => animation.cancel();
  }, [identity, instant, ref]);
}
